import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Undo,
    Redo,
    AlignLeft,
    AlignCenter,
    AlignRight,
    List,
    ListOrdered,
    Link as LinkIcon,
    Image as ImageIcon
} from 'lucide-react';

type RichTextEditorHandle = { getHTML: () => string; setHTML?: (html: string) => void };

type RichTextEditorProps = {
    value?: string;
    placeholder?: string;
    isDark?: boolean;
    onChange?: (html: string) => void;
    suspendExternalSync?: boolean;
    withSeparators?: boolean;
    compactSpacing?: boolean;
    embedded?: boolean;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const normalizeHex = (v: string): string | null => {
    try {
        let s = v.trim();
        if (!s) return null;
        if (s[0] !== '#') s = '#' + s;
        if (s.length === 4) {
            const r = s[1], g = s[2], b = s[3];
            s = `#${r}${r}${g}${g}${b}${b}`;
        }
        if (/^#([0-9a-f]{6})$/i.test(s)) return s.toLowerCase();
        return null;
    } catch {
        return null;
    }
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const m = normalizeHex(hex);
    if (!m) return null;
    const r = parseInt(m.slice(1, 3), 16);
    const g = parseInt(m.slice(3, 5), 16);
    const b = parseInt(m.slice(5, 7), 16);
    return { r, g, b };
};

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }): string => {
    const to = (x: number) => clamp(Math.round(x), 0, 255).toString(16).padStart(2, '0');
    return `#${to(r)}${to(g)}${to(b)}`;
};

const rgbToHsv = ({ r, g, b }: { r: number; g: number; b: number }): { h: number; s: number; v: number } => {
    const rr = r / 255, gg = g / 255, bb = b / 255;
    const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        switch (max) {
            case rr: h = ((gg - bb) / d) % 6; break;
            case gg: h = (bb - rr) / d + 2; break;
            default: h = (rr - gg) / d + 4; break;
        }
        h *= 60; if (h < 0) h += 360;
    }
    const s = max === 0 ? 0 : d / max;
    const v = max;
    return { h, s, v };
};

const hsvToRgb = ({ h, s, v }: { h: number; s: number; v: number }): { r: number; g: number; b: number } => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    let rr = 0, gg = 0, bb = 0;
    if (h >= 0 && h < 60) { rr = c; gg = x; bb = 0; }
    else if (h < 120) { rr = x; gg = c; bb = 0; }
    else if (h < 180) { rr = 0; gg = c; bb = x; }
    else if (h < 240) { rr = 0; gg = x; bb = c; }
    else if (h < 300) { rr = x; gg = 0; bb = c; }
    else { rr = c; gg = 0; bb = x; }
    return { r: (rr + m) * 255, g: (gg + m) * 255, b: (bb + m) * 255 };
};

const colorToHex = (value: string): string | undefined => {
    try {
        if (!value) return undefined;
        if (value.startsWith('#')) {
            return normalizeHex(value) ?? value.toLowerCase();
        }
        const el = document.createElement('span');
        el.style.color = value;
        document.body.appendChild(el);
        const rgb = getComputedStyle(el).color;
        if (el.parentNode) document.body.removeChild(el);
        const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (!m) return undefined;
        const r = Number(m[1]).toString(16).padStart(2, '0');
        const g = Number(m[2]).toString(16).padStart(2, '0');
        const b = Number(m[3]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`.toLowerCase();
    } catch {
        return undefined;
    }
};

const RichTextToolbar = ({
    onCommand,
    activeFormats,
    isDark = false,
    currentColor,
    withSeparators = false,
    compactSpacing = false
}: {
    onCommand: (command: string) => void;
    activeFormats: Set<string>;
    isDark?: boolean;
    currentColor?: string;
    withSeparators?: boolean;
    compactSpacing?: boolean;
}) => {
    const dark = typeof isDark === 'boolean'
        ? isDark
        : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    const btnBase = `${dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-700 hover:bg-zinc-100'} inline-flex items-center justify-center w-8 h-8 rounded-md`;
    const btnActive = `${dark ? 'bg-zinc-700 text-zinc-50' : 'bg-zinc-200 text-zinc-900'}`;
    const Section = ({ children, divider = false, mrClass = 'mr-2' }: { children: React.ReactNode; divider?: boolean; mrClass?: string }) => (
        <div className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} flex items-center gap-1 pr-2 ${mrClass} ${divider ? 'border-r' : ''}`}>{children}</div>
    );

    const keepSelectionMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        e.preventDefault();
    };

    const [colorOpen, setColorOpen] = useState(false);
    const suppressColorToggleRef = useRef(false);
    const colorWrapRef = useRef<HTMLDivElement | null>(null);
    const [showAdvancedColor, setShowAdvancedColor] = useState(false);
    const [isPickingColor, setIsPickingColor] = useState(false);
    const [customHex, setCustomHex] = useState<string>('#000000');
    const [hsv, setHsv] = useState<{ h: number; s: number; v: number }>({ h: 0, s: 0, v: 0 });
    const svRef = useRef<HTMLDivElement | null>(null);
    const hueRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (!colorOpen) return;
            if (isPickingColor) return;
            const wrap = colorWrapRef.current;
            if (wrap && wrap.contains(e.target as Node)) return;
            setColorOpen(false);
            setShowAdvancedColor(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setColorOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [colorOpen, isPickingColor]);

    useEffect(() => {
        if (colorOpen) {
            const hx = (currentColor && /^#([0-9a-f]{6})$/i.test(currentColor)) ? currentColor : '#000000';
            setCustomHex(hx);
            const rgb = hexToRgb(hx) || { r: 0, g: 0, b: 0 };
            setHsv(rgbToHsv(rgb));
        } else {
            setShowAdvancedColor(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colorOpen]);

    const applyAndCloseColor = (hx: string) => {
        onCommand(`foreColor|${hx}`);
        setIsPickingColor(false);
        setShowAdvancedColor(false);
        suppressColorToggleRef.current = true;
        setColorOpen(false);
        setTimeout(() => { suppressColorToggleRef.current = false; }, 0);
    };

    return (
        <div className={`p-2 border-b ${dark ? 'border-zinc-800' : 'border-zinc-200'} flex flex-wrap items-center`}>
            <Section divider={withSeparators}>
                <button type="button" title="Bold (Ctrl+B)" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('bold')} className={`${btnBase} ${activeFormats.has('bold') ? btnActive : ''}`}><Bold className="w-4 h-4" /></button>
                <button type="button" title="Italic (Ctrl+I)" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('italic')} className={`${btnBase} ${activeFormats.has('italic') ? btnActive : ''}`}><Italic className="w-4 h-4" /></button>
                <button type="button" title="Underline (Ctrl+U)" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('underline')} className={`${btnBase} ${activeFormats.has('underline') ? btnActive : ''}`}><Underline className="w-4 h-4" /></button>
                <button type="button" title="Strikethrough" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('strikeThrough')} className={`${btnBase} ${activeFormats.has('strikeThrough') ? btnActive : ''}`}><Strikethrough className="w-4 h-4" /></button>
            </Section>
            <span className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} mx-2 h-5 border-l`} aria-hidden />
            <Section divider={withSeparators}>
                <button type="button" title="Undo (Ctrl+Z)" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('undo')} className={btnBase}><Undo className="w-4 h-4" /></button>
                <button type="button" title="Redo (Ctrl+Y / Ctrl+Shift+Z)" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('redo')} className={btnBase}><Redo className="w-4 h-4" /></button>
                <button type="button" title="Insert link" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('createLink')} className={`${btnBase} ${activeFormats.has('createLink') ? btnActive : ''}`}><LinkIcon className="w-4 h-4" /></button>
                <button type="button" title="Insert image" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertImage')} className={btnBase}><ImageIcon className="w-4 h-4" /></button>
            </Section>
            

            {/* Right-aligned: alignment and list controls */}
            <div className="ml-auto">
                <Section divider={withSeparators} mrClass={compactSpacing ? 'mr-0.5' : 'mr-2'}>
                    <label className="sr-only" htmlFor="rte-font-size">Text size</label>
                    <select
                        id="rte-font-size"
                        title="Text size"
                        onChange={(e) => onCommand(`fontSize|${e.target.value}`)}
                        defaultValue="3"
                        className={`${dark ? 'bg-zinc-900 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-800'} h-8 rounded-md border px-1 text-xs`}
                    >
                        <option value="2">Small</option>
                        <option value="3">Normal</option>
                        <option value="4">Medium</option>
                        <option value="5">Large</option>
                    </select>

                    <label className="sr-only ml-2" htmlFor="rte-font-family">Font family</label>
                    <select
                        id="rte-font-family"
                        title="Font family"
                        onChange={(e) => onCommand(`fontName|${e.target.value}`)}
                        defaultValue=""
                        className={`${dark ? 'bg-zinc-900 border-zinc-700 text-zinc-200' : 'bg-white border-zinc-300 text-zinc-800'} h-8 rounded-md border px-1 text-xs ml-2`}
                    >
                        <option value="">Font</option>
                        <option value="Arial, Helvetica, sans-serif">Arial</option>
                        <option value="'Helvetica Neue', Helvetica, Arial, sans-serif">Helvetica</option>
                        <option value="Georgia, 'Times New Roman', Times, serif">Georgia</option>
                        <option value="'Times New Roman', Times, serif">Times New Roman</option>
                        <option value="'Courier New', Courier, monospace">Courier New</option>
                        <option value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</option>
                        <option value="Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto">Inter</option>
                        {/* Cursive / Script / Decorative fonts */}
                        <option value="'Brush Script MT', 'Brush Script Std', cursive">Brush Script</option>
                        <option value="'Dancing Script', cursive">Dancing Script</option>
                        <option value="Pacifico, cursive">Pacifico</option>
                        <option value="Lobster, cursive">Lobster</option>
                        <option value="'Great Vibes', cursive">Great Vibes</option>
                        <option value="'Playfair Display', serif">Playfair Display</option>
                        <option value="Garamond, serif">Garamond</option>
                        <option value="'Comic Sans MS', 'Comic Sans', cursive">Comic Sans</option>
                    </select>

                    <div ref={colorWrapRef} className="relative inline-block">
                        <button
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={colorOpen}
                            aria-label="Text color"
                            title="Text color"
                            onMouseDown={keepSelectionMouseDown}
                            onClick={() => {
                                if (suppressColorToggleRef.current) return;
                                setColorOpen((s) => !s);
                            }}
                            className={`${btnBase} relative`}
                        >
                            <span
                                aria-hidden
                                className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} inline-block w-5 h-5 rounded-sm border`}
                                style={{ backgroundColor: currentColor || '#000000' }}
                            />
                        </button>
                        {colorOpen && (
                            <div
                                className={`absolute top-full right-0 mt-1 z-50 ${dark ? 'bg-zinc-900 border border-zinc-800 text-zinc-100' : 'bg-white border border-zinc-200 text-zinc-900'} rounded-md shadow-lg p-2 min-w-[12rem]`}
                                aria-label="Choose text color"
                            >
                                {!showAdvancedColor ? (
                                    <>
                                        <div className="grid grid-cols-8 gap-1 p-1">
                                            {[
                                                '#000000', '#4b5563', '#6b7280', '#9ca3af', '#d1d5db', '#ffffff',
                                                '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6',
                                                '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
                                            ].map((c) => (
                                                <button
                                                    key={c}
                                                    type="button"
                                                    title={c}
                                                    aria-label={`Color ${c}`}
                                                    onMouseDown={keepSelectionMouseDown}
                                                    onClick={() => {
                                                        onCommand(`foreColor|${c}`);
                                                        setColorOpen(false);
                                                    }}
                                                    className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} w-5 h-5 rounded-sm border hover:ring-2 hover:ring-blue-500`}
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onMouseDown={keepSelectionMouseDown}
                                            className={`${dark ? 'text-zinc-200 hover:bg-zinc-800' : 'text-zinc-700 hover:bg-zinc-100'} w-full text-left px-2 py-1 rounded-md text-sm`}
                                            onClick={() => {
                                                setShowAdvancedColor(true);
                                                const hx = (currentColor && /^#([0-9a-f]{6})$/i.test(currentColor)) ? currentColor : '#000000';
                                                setCustomHex(hx);
                                                const rgb = hexToRgb(hx) || { r: 0, g: 0, b: 0 };
                                                setHsv(rgbToHsv(rgb));
                                            }}
                                        >
                                            Custom…
                                        </button>
                                    </>
                                ) : (
                                    <div className="p-2 space-y-3" onMouseDown={(e) => { e.stopPropagation(); }}>
                                        <div
                                            ref={svRef}
                                            className="relative w-48 h-32 cursor-crosshair rounded-sm overflow-hidden"
                                            style={{
                                                background: `linear-gradient(to top, black, transparent), linear-gradient(to right, white, hsl(${hsv.h}, 100%, 50%))`
                                            }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsPickingColor(true);
                                                const box = svRef.current!.getBoundingClientRect();
                                                const update = (clientX: number, clientY: number) => {
                                                    const s = clamp((clientX - box.left) / box.width, 0, 1);
                                                    const v = clamp(1 - (clientY - box.top) / box.height, 0, 1);
                                                    const next = { h: hsv.h, s, v };
                                                    setHsv(next);
                                                    const hex = rgbToHex(hsvToRgb(next));
                                                    setCustomHex(hex);
                                                    const hx = normalizeHex(hex);
                                                    if (hx) onCommand(`foreColor|${hx}`);
                                                };
                                                update(e.clientX, e.clientY);
                                                const onMove = (ev: MouseEvent) => update(ev.clientX, ev.clientY);
                                                const onUp = () => {
                                                    document.removeEventListener('mousemove', onMove);
                                                    document.removeEventListener('mouseup', onUp);
                                                    setIsPickingColor(false);
                                                };
                                                document.addEventListener('mousemove', onMove);
                                                document.addEventListener('mouseup', onUp);
                                            }}
                                        >
                                            <div
                                                className="absolute w-3 h-3 rounded-full border border-white shadow"
                                                style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                            />
                                        </div>
                                        <div
                                            ref={hueRef}
                                            className="relative w-48 h-3 rounded-sm cursor-pointer"
                                            style={{ background: 'linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)' }}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsPickingColor(true);
                                                const box = hueRef.current!.getBoundingClientRect();
                                                const update = (clientX: number) => {
                                                    const x = clamp((clientX - box.left) / box.width, 0, 1);
                                                    const h = x * 360;
                                                    const next = { ...hsv, h };
                                                    setHsv(next);
                                                    const hex = rgbToHex(hsvToRgb(next));
                                                    setCustomHex(hex);
                                                    const hx = normalizeHex(hex);
                                                    if (hx) onCommand(`foreColor|${hx}`);
                                                };
                                                update(e.clientX);
                                                const onMove = (ev: MouseEvent) => update(ev.clientX);
                                                const onUp = () => {
                                                    document.removeEventListener('mousemove', onMove);
                                                    document.removeEventListener('mouseup', onUp);
                                                    setIsPickingColor(false);
                                                };
                                                document.addEventListener('mousemove', onMove);
                                                document.addEventListener('mouseup', onUp);
                                            }}
                                        >
                                            <div
                                                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border border-white shadow"
                                                style={{ left: `${(hsv.h / 360) * 100}%`, transform: 'translate(-50%, -50%)' }}
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className={`${dark ? 'border-zinc-700' : 'border-zinc-300'} w-6 h-6 rounded-sm border`} style={{ backgroundColor: normalizeHex(customHex) || '#000000' }} />
                                            <input
                                                type="text"
                                                value={customHex}
                                                onChange={(e) => {
                                                    const v = e.target.value.replace(/[^#0-9a-fA-F]/g, '');
                                                    const val = v.startsWith('#') ? v : ('#' + v).replace(/##+/, '#');
                                                    setCustomHex(val);
                                                    const rgb = hexToRgb(val);
                                                    if (rgb) setHsv(rgbToHsv(rgb));
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const hx = normalizeHex(customHex);
                                                        if (hx) applyAndCloseColor(hx);
                                                    }
                                                }}
                                                className={`${dark ? 'bg-zinc-900 border-zinc-700 text-zinc-100' : 'bg-white border-zinc-300 text-zinc-900'} flex-1 px-2 py-1 rounded-md border text-sm`}
                                                placeholder="#rrggbb"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                            <button
                                                type="button"
                                                className={`${dark ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100' : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-800'} px-2 py-1 rounded-md text-sm`}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setIsPickingColor(false);
                                                    setShowAdvancedColor(false);
                                                    suppressColorToggleRef.current = true;
                                                    setColorOpen(false);
                                                    setTimeout(() => { suppressColorToggleRef.current = false; }, 0);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className={`px-2 py-1 rounded-md text-sm ${dark ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const hx = normalizeHex(customHex);
                                                    if (hx) applyAndCloseColor(hx);
                                                }}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <span className={`${dark ? 'border-zinc-800' : 'border-zinc-200'} mx-2 h-5 border-l`} aria-hidden />

                    <button type="button" title="Align left" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyLeft')} className={`${btnBase} ${activeFormats.has('justifyLeft') ? btnActive : ''}`}><AlignLeft className="w-4 h-4" /></button>
                    <button type="button" title="Align center" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyCenter')} className={`${btnBase} ${activeFormats.has('justifyCenter') ? btnActive : ''}`}><AlignCenter className="w-4 h-4" /></button>
                    <button type="button" title="Align right" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('justifyRight')} className={`${btnBase} ${activeFormats.has('justifyRight') ? btnActive : ''}`}><AlignRight className="w-4 h-4" /></button>
                    <button type="button" title="Bulleted list" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertUnorderedList')} className={`${btnBase} ${activeFormats.has('insertUnorderedList') ? btnActive : ''}`}><List className="w-4 h-4" /></button>
                    <button type="button" title="Numbered list" onMouseDown={keepSelectionMouseDown} onClick={() => onCommand('insertOrderedList')} className={`${btnBase} ${activeFormats.has('insertOrderedList') ? btnActive : ''}`}><ListOrdered className="w-4 h-4" /></button>
                </Section>
            </div>
        </div>
    );
};

export const RichTextEditor = React.forwardRef(function RichTextEditor(
    {
        value = '',
        placeholder,
        isDark,
        onChange,
        suspendExternalSync = false,
        withSeparators = false,
        compactSpacing = false,
        embedded = false
    }: RichTextEditorProps,
    ref: React.Ref<RichTextEditorHandle | null>
) {
    const [activeFormats, setActiveFormats] = useState(new Set<string>());
    const [currentColor, setCurrentColor] = useState<string | undefined>(undefined);
    const editableRef = useRef<HTMLDivElement | null>(null);
    const imageInputRef = useRef<HTMLInputElement | null>(null);
    const savedRangeRef = useRef<Range | null>(null);
    const [localHTML, setLocalHTML] = useState<string>(value || '');

    useEffect(() => {
        if (suspendExternalSync) return;
        const root = editableRef.current;
        const active = document.activeElement;
        if (root && active && root.contains(active)) return;
        setLocalHTML(value || '');
    }, [value, suspendExternalSync]);

    useEffect(() => {
        if (!editableRef.current) return;
        const current = editableRef.current.innerHTML || '';
        if (current === localHTML) return;
        const active = document.activeElement;
        if (active && editableRef.current.contains(active)) return;
        editableRef.current.innerHTML = localHTML;
    }, [localHTML]);

    const restoreSelectionIntoEditor = useCallback(() => {
        const editor = editableRef.current;
        const r = savedRangeRef.current;
        if (!editor || !r) return;
        try {
            const sel = window.getSelection();
            if (!sel) return;
            sel.removeAllRanges();
            sel.addRange(r);
            editor.focus();
        } catch {}
    }, []);

    const escapeHtml = (unsafe: string) => {
        return unsafe
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    };

    const linkifyTextToHtml = (text: string) => {
        if (!text) return '';
        const re = /((https?:\/\/|www\.)[^\s]+)/g;
        let lastIndex = 0;
        const parts: string[] = [];
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
            const idx = m.index;
            const url = m[0];
            const before = text.slice(lastIndex, idx);
            parts.push(escapeHtml(before).replace(/\n/g, '<br>'));
            let href = url;
            if (!/^https?:\/\//i.test(href)) href = 'http://' + href;
            parts.push(`<a href="${href}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`);
            lastIndex = idx + url.length;
        }
        const tail = text.slice(lastIndex);
        parts.push(escapeHtml(tail).replace(/\n/g, '<br>'));
        return parts.join('');
    };

    const exec = (cmd: string, val?: string) => {
        try {
            document.execCommand(cmd, false, val);
            editableRef.current?.focus();
        } catch {}
    };

    const updateActiveFromSelection = useCallback(() => {
        const sel = document.getSelection();
        const root = editableRef.current;
        if (!sel || !root) return;
        const anchor = sel.anchorNode;
        if (!anchor || !root.contains(anchor)) return;
        const next = new Set<string>();
        try { if (document.queryCommandState('bold')) next.add('bold'); } catch {}
        try { if (document.queryCommandState('italic')) next.add('italic'); } catch {}
        try { if (document.queryCommandState('underline')) next.add('underline'); } catch {}
        try { if (document.queryCommandState('strikeThrough')) next.add('strikeThrough'); } catch {}
        try { if (document.queryCommandState('insertUnorderedList')) next.add('insertUnorderedList'); } catch {}
        try { if (document.queryCommandState('insertOrderedList')) next.add('insertOrderedList'); } catch {}
        try { if (document.queryCommandState('justifyLeft')) next.add('justifyLeft'); } catch {}
        try { if (document.queryCommandState('justifyCenter')) next.add('justifyCenter'); } catch {}
        try { if (document.queryCommandState('justifyRight')) next.add('justifyRight'); } catch {}
        setActiveFormats(next);
        try {
            const val = document.queryCommandValue('foreColor') as string;
            setCurrentColor(colorToHex(val));
        } catch {}
        try {
            if (sel.rangeCount > 0) {
                const r = sel.getRangeAt(0);
                if (root.contains(r.startContainer) || root.contains(r.endContainer)) {
                    savedRangeRef.current = r.cloneRange();
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        const onSel = () => updateActiveFromSelection();
        document.addEventListener('selectionchange', onSel);
        return () => document.removeEventListener('selectionchange', onSel);
    }, [updateActiveFromSelection]);

    const handleCommand = (command: string) => {
        const [cmd, arg] = command.split('|');
        switch (cmd) {
            case 'bold':
            case 'italic':
            case 'underline':
            case 'strikeThrough':
            case 'justifyLeft':
            case 'justifyCenter':
            case 'justifyRight':
                restoreSelectionIntoEditor();
                exec(cmd);
                return;
            case 'undo':
            case 'redo':
                restoreSelectionIntoEditor();
                exec(cmd);
                return;
            case 'insertUnorderedList':
            case 'insertOrderedList': {
                restoreSelectionIntoEditor();
                exec(cmd);
                // If execCommand didn't create a list (some browsers/dev contexts),
                // create one around the current selection/caret.
                setTimeout(() => {
                    const sel = window.getSelection();
                    if (!sel || sel.rangeCount === 0) return;
                    const range = sel.getRangeAt(0);
                    const editor = editableRef.current;
                    if (!editor) return;

                    // If selection is already inside a <li>, nothing to do
                    let anc: Element | null = range.startContainer.nodeType === 3 ? (range.startContainer.parentElement as Element) : (range.startContainer as Element);
                    while (anc && anc !== editor && anc.tagName !== 'LI') anc = anc.parentElement;
                    if (anc && anc.tagName === 'LI') return;

                    const listTag = cmd === 'insertUnorderedList' ? 'ul' : 'ol';
                    const list = document.createElement(listTag);
                    const li = document.createElement('li');

                    if (range.collapsed) {
                        // Insert an empty list with one empty item and place caret inside
                        const br = document.createElement('br');
                        li.appendChild(br);
                        list.appendChild(li);
                        // Insert list at caret
                        range.deleteContents();
                        range.insertNode(list);
                        // Put caret inside the li
                        const newRange = document.createRange();
                        newRange.setStart(li, li.childNodes.length);
                        newRange.collapse(true);
                        sel.removeAllRanges();
                        sel.addRange(newRange);
                    } else {
                        // Wrap the selected contents into a list item
                        try {
                            const frag = range.extractContents();
                            li.appendChild(frag);
                            list.appendChild(li);
                            range.insertNode(list);
                            // Place caret at end of inserted li
                            const newRange = document.createRange();
                            newRange.setStart(li, li.childNodes.length);
                            newRange.collapse(true);
                            sel.removeAllRanges();
                            sel.addRange(newRange);
                        } catch {
                            // Fallback: insert a single empty list
                            const br = document.createElement('br');
                            li.appendChild(br);
                            list.appendChild(li);
                            range.insertNode(list);
                        }
                    }
                    // Update editor HTML state
                    const html = editableRef.current?.innerHTML ?? '';
                    setLocalHTML(html);
                    if (onChange) onChange(html);
                }, 50);
                return;
            }
            case 'fontSize':
                if (arg) { restoreSelectionIntoEditor(); exec('fontSize', arg); }
                return;
            case 'fontName':
                if (arg) { restoreSelectionIntoEditor(); exec('fontName', arg); }
                return;
            case 'foreColor':
                if (arg) { restoreSelectionIntoEditor(); exec('foreColor', arg); }
                return;
            case 'createLink': {
                const url = window.prompt('Enter URL', 'https://') || '';
                if (!url) return;
                restoreSelectionIntoEditor();
                exec('createLink', url);
                // Ensure created links open in a new tab and are safe
                setTimeout(() => {
                    try {
                        const editor = editableRef.current;
                        if (!editor) return;
                        const anchors = Array.from(editor.querySelectorAll('a')) as HTMLAnchorElement[];
                        anchors.forEach(a => {
                            if (!a.getAttribute('target')) a.setAttribute('target', '_blank');
                            if (!a.getAttribute('rel')) a.setAttribute('rel', 'noopener noreferrer');
                        });
                    } catch {}
                }, 0);
                return;
            }
            case 'insertImage': {
                imageInputRef.current?.click();
                return;
            }
            default:
                return;
        }
    };

    const handleInput = () => {
        const html = editableRef.current?.innerHTML ?? '';
        setLocalHTML(html);
        if (onChange) onChange(html);
        updateActiveFromSelection();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        restoreSelectionIntoEditor();
        try {
            const html = linkifyTextToHtml(text);
            if (html && html !== escapeHtml(text).replace(/\n/g, '<br>')) {
                document.execCommand('insertHTML', false, html);
            } else {
                document.execCommand('insertText', false, text);
            }
        } catch {
            document.execCommand('insertText', false, text);
        }

        // Ensure all anchors open in new tabs safely
        setTimeout(() => {
            try {
                const editor = editableRef.current;
                if (!editor) return;
                const anchors = Array.from(editor.querySelectorAll('a')) as HTMLAnchorElement[];
                anchors.forEach(a => {
                    if (!a.getAttribute('target')) a.setAttribute('target', '_blank');
                    if (!a.getAttribute('rel')) a.setAttribute('rel', 'noopener noreferrer');
                });
            } catch {}
        }, 0);

        const html = editableRef.current?.innerHTML ?? '';
        setLocalHTML(html);
        if (onChange) onChange(html);
    };

    const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = String(reader.result || '');
            restoreSelectionIntoEditor();
            exec('insertImage', dataUrl);
            const html = editableRef.current?.innerHTML ?? '';
            setLocalHTML(html);
            if (onChange) onChange(html);
            e.target.value = '';
        };
        reader.readAsDataURL(file);
    };

    useImperativeHandle(ref, () => ({
        getHTML: () => editableRef.current?.innerHTML ?? '',
        setHTML: (html: string) => {
            setLocalHTML(html);
            if (editableRef.current) editableRef.current.innerHTML = html;
        }
    }), []);

    const dark = typeof isDark === 'boolean'
        ? isDark
        : (typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

    const containerClass = embedded
        ? 'relative'
        : 'relative bg-white dark:bg-zinc-800/50 rounded-xl border border-zinc-200 dark:border-zinc-700/50 focus-within:ring-2 focus-within:ring-blue-500/50 transition-shadow';

    return (
        <div className={containerClass}>
            <RichTextToolbar
                onCommand={handleCommand}
                activeFormats={activeFormats}
                isDark={dark}
                currentColor={currentColor}
                withSeparators={withSeparators}
                compactSpacing={compactSpacing}
            />

            <div
                ref={editableRef}
                className={`content-editable-area w-full min-h-[120px] p-3 text-sm outline-none focus:outline-none leading-relaxed overflow-y-auto break-words whitespace-pre-wrap ${dark ? 'text-zinc-100' : 'text-zinc-900'} ${embedded ? '' : ''}`}
                contentEditable
                suppressContentEditableWarning
                data-placeholder={placeholder || ''}
                onInput={handleInput}
                onPaste={handlePaste}
                onMouseUp={updateActiveFromSelection}
                onKeyUp={updateActiveFromSelection}
                onClick={(e) => {
                    try {
                        const target = e.target as HTMLElement;
                        const anchor = target.closest && (target.closest('a') as HTMLAnchorElement | null);
                        if (anchor && anchor.href) {
                            // Open link in new tab and prevent editor focus/navigation
                            window.open(anchor.href, '_blank', 'noopener');
                            e.preventDefault();
                        }
                    } catch {}
                }}
            />

            <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                aria-label="Insert image"
                title="Insert image"
                className="hidden"
                onChange={handleImageFile}
            />
        </div>
    );
});

export default RichTextEditor;